/**
 * A funtion to shorten a project title to a human-readable short form (maxWords) generated by gpt-5-mini
 * Heuristics:
 * - Preserve conjunction/title patterns (e.g., "The Beauty & The Beast").
 * - Prefer segments that look like brand/name (all-caps, short segments).
 * - Prefer proper-noun clusters (consecutive TitleCase tokens).
 * - If an action keyword (renovated/restoration/etc.) is present, prefer "<MainNoun> Renovation".
 * - Otherwise use title-like tokens in original order (no re-ordering).
 */
export function shortenProjectTitle(title: string, maxWords = 3): string {
    const STOPWORDS = new Set([
        'the',
        'a',
        'an',
        'and',
        'of',
        'in',
        'on',
        'for',
        'to',
        'by',
        'with',
        'at',
        'from',
        'is',
        'that',
        'this',
        'our',
        'new',
    ]);

    if (!title || !title.trim()) return '';

    const trimmed = title.trim();

    // 1) Preserve explicit title-like conjunctions: "The X & The Y" or "X and Y"
    // Use Unicode-aware regex to detect title-like conjunctions (handles characters like Ō)
    const titleConjRegex =
        /\b(?:(?:The|A|An)\s+)?\p{Lu}[\p{L}\p{M}'`’-]*(?:\s+(?:&|and)\s+(?:(?:The|A|An)\s+)?\p{Lu}[\p{L}\p{M}'`’-]*)+/u;
    const conjMatch = trimmed.match(titleConjRegex);
    if (conjMatch) return conjMatch[0].trim();

    // split on common separators (prefer the first matching separator)
    const separators = [
        ':',
        ' — ',
        ' – ',
        ' - ',
        ' —',
        '–',
        '-',
        '|',
        ' / ',
        '/',
    ];
    let segments = [trimmed];
    for (const sep of separators) {
        if (trimmed.includes(sep)) {
            segments = trimmed
                .split(sep)
                .map((s) => s.trim())
                .filter(Boolean);
            break;
        }
    }

    // Unicode-aware token regex: include letters, marks (combining), numbers and common punctuation
    const tokenRegex = /[\p{L}\p{M}\p{N}'’&]+/gu;

    function tokensOf(s?: string): string[] {
        if (!s) return [];
        return s.match(tokenRegex) || [];
    }

    function isTitleLike(word: string): boolean {
        if (word.length <= 1) return false;
        // Treat full-uppercased words as acronyms if they contain any uppercase letter
        if (word === word.toUpperCase() && /\p{Lu}/u.test(word)) return true; // ACRONYM
        // Title-like: starts with an uppercase letter followed by lowercase letters (Unicode-aware)
        return /^[\p{Lu}][\p{Ll}\p{M}'’-]+$/u.test(word);
    }

    // domain keywords to bump score (Church, House, Renovation, Restoration, Project, Resort, Apartment, Villa, Kitchen, Garden)
    const IMPORTANT_NOUNS = new Set([
        'church',
        'house',
        'renovation',
        'restoration',
        'project',
        'resort',
        'apartment',
        'villa',
        'kitchen',
        'garden',
        'studio',
        'office',
        'loft',
        'extension',
        'pod',
        'apts',
        'hotel',
    ]);

    // action keywords mapped to preferred noun form
    const ACTION_MAP: Record<string, string> = {
        renovated: 'Renovation',
        renovation: 'Renovation',
        restored: 'Restoration',
        restoration: 'Restoration',
        refurbish: 'Refurbishment',
        refurb: 'Refurbishment',
        restorationsof: 'Restoration',
        extension: 'Extension',
        extensioned: 'Extension',
    };

    function containsAllCapsToken(s: string): boolean {
        return tokensOf(s).some(
            (t) => t.length > 1 && t === t.toUpperCase() && /\p{Lu}/u.test(t)
        );
    }

    function safeLower(s?: string) {
        return (s ?? '').toLowerCase();
    }

    // Score a segment with improved heuristics
    function scoreSegment(s: string): number {
        const tokens = tokensOf(s);
        if (tokens.length === 0) return 0;
        let score = 0;

        // big boost for brand/all-caps token (prefer OASIS)
        if (containsAllCapsToken(s)) score += 8;

        // boost for short segments (1-3 tokens) that look like names
        if (tokens.length <= 3) score += 3;

        // boost for consecutive TitleCase tokens (proper noun cluster)
        let consec = 0;
        let maxConsec = 0;
        for (const t of tokens) {
            if (isTitleLike(t)) {
                consec++;
                maxConsec = Math.max(maxConsec, consec);
            } else consec = 0;
        }
        score += maxConsec * 4;

        // boost for important nouns
        for (const t of tokens) {
            if (IMPORTANT_NOUNS.has(safeLower(t))) score += 3;
            if (Object.keys(ACTION_MAP).includes(safeLower(t))) score += 2;
        }

        // penalise very long segments (less likely to be short name)
        score -= Math.max(0, tokens.length - 6) * 0.6;

        // baseline token scoring (as before) to retain preference toward meaningful words
        for (const t of tokens) {
            const lc = t.toLowerCase();
            if (STOPWORDS.has(lc) || /^[0-9]+$/.test(t)) continue;
            if (isTitleLike(t)) score += 1;
            else if (t.length >= 4) score += 0.4;
        }

        return score;
    }

    // pick best segment
    let bestSegment = segments[0];
    let bestScore = -Infinity;
    for (const seg of segments) {
        const s = scoreSegment(seg);
        if (s > bestScore) {
            bestScore = s;
            bestSegment = seg;
        }
    }

    // Prefer the original leading segment if it's short and clearly title-like.
    // This helps cases like "Homestead - North Otago" -> prefer "Homestead".
    const firstSegment = segments[0];
    if (firstSegment && firstSegment !== bestSegment) {
        const firstTokens = tokensOf(firstSegment);
        const isShortTitleLike =
            firstTokens.length > 0 &&
            firstTokens.length <= 3 &&
            firstTokens.some((t) => isTitleLike(t));
        if (isShortTitleLike) {
            bestSegment = firstSegment;
        }
    }

    function finalizeOutput(s: string) {
        if (!s) return s;
        // Unicode-aware capitalize of the first grapheme
        return s.replace(/^./u, (c) => c.toUpperCase());
    }

    // Prefer returning phrases like "Screened by Pohutukawa" when present
    // Unicode-aware "... by ..." capture (handles diacritics)
    const byPhraseRegex =
        /\b([\p{Lu}][\p{L}\p{M}'`’-]*(?:\s+[\p{Lu}][\p{L}\p{M}'`’-]*)*\s+by\s+(?:The\s+)?[\p{Lu}][\p{L}\p{M}'`’-]*)/u;
    const byMatch = (bestSegment ?? '').match(byPhraseRegex);
    const byCaptured = byMatch && byMatch[1];
    if (byCaptured) return finalizeOutput(byCaptured.trim());

    const segTokens = tokensOf(bestSegment);

    // If an action keyword exists, try to form "<MainNoun> <ActionNoun>" for readability
    const lowerTokens = segTokens.map((t) => safeLower(t));
    const actionIndex = lowerTokens.findIndex((t) =>
        Object.keys(ACTION_MAP).includes(t)
    );
    if (actionIndex >= 0) {
        // find main noun candidate (prefer a 2-token cluster immediately before action when present,
        // else nearest noun-like token before action, else first non-stopword)
        let mainNoun = '';
        // prefer two-token cluster before action (e.g., "Washington Ave Renovation")
        if (actionIndex - 2 >= 0) {
            const t1 = segTokens[actionIndex - 2] ?? '';
            const t2 = segTokens[actionIndex - 1] ?? '';
            if (
                !STOPWORDS.has(safeLower(t1)) &&
                !STOPWORDS.has(safeLower(t2)) &&
                (isTitleLike(t1) ||
                    isTitleLike(t2) ||
                    (t1.length > 2 && t2.length > 2))
            ) {
                mainNoun = `${t1} ${t2}`;
            }
        }
        if (!mainNoun) {
            for (let i = actionIndex - 1; i >= 0; i--) {
                const cand = segTokens[i] ?? '';
                const lc = safeLower(cand);
                if (!STOPWORDS.has(lc) && cand.length > 1) {
                    mainNoun = cand;
                    break;
                }
            }
        }
        if (!mainNoun) {
            for (const t of segTokens) {
                const token = t ?? '';
                if (!STOPWORDS.has(safeLower(token)) && token.length > 1) {
                    mainNoun = token;
                    break;
                }
            }
        }
        const actionToken = safeLower(segTokens[actionIndex]);
        const actionNoun =
            ACTION_MAP[actionToken] ||
            ACTION_MAP[actionToken.replace(/ed$/, '')] ||
            'Renovation';
        if (mainNoun) {
            // preserve leading article if exists before mainNoun
            const mainNounIndex = segTokens.indexOf(mainNoun);
            const before =
                mainNounIndex > 0 ? segTokens.slice(0, mainNounIndex) : [];
            const leadingArticle = /^(the|a|an)$/i.test(
                before[before.length - 1] ?? ''
            )
                ? (before[before.length - 1] ?? '')
                : '';
            return (
                (leadingArticle ? leadingArticle + ' ' : '') +
                capitalize(mainNoun) +
                ' ' +
                actionNoun
            );
        }
        // fallback to just action noun
        return actionNoun;
    }

    // If there is a proper-noun cluster (2+ TitleLike tokens in order), keep that cluster (and trailing small nouns like Church/House)
    const clusters: string[][] = [];
    let current: string[] = [];
    for (let i = 0; i < segTokens.length; i++) {
        const t = segTokens[i] ?? '';
        if (isTitleLike(t) || /^[A-Z][a-zA-Z0-9'.-]*$/.test(t)) {
            current.push(t);
        } else {
            if (current.length) {
                clusters.push(current.slice());
                current = [];
            }
        }
    }
    if (current.length) clusters.push(current);

    // find longest cluster with length >=2
    let bestCluster: string[] | null = null;
    for (const c of clusters) {
        if (c.length >= 2 && (!bestCluster || c.length > bestCluster.length))
            bestCluster = c;
    }
    if (bestCluster) {
        // include any immediate following important noun (e.g., "St", "Mary's", "Church")
        const startIndex = indexOfSubarray(segTokens, bestCluster);
        if (startIndex >= 0) {
            let endIndex = startIndex + bestCluster.length - 1;
            // include next token if it's an important noun like 'Church' or possessive (Mary's)
            if (
                endIndex + 1 < segTokens.length &&
                IMPORTANT_NOUNS.has(safeLower(segTokens[endIndex + 1] ?? ''))
            ) {
                endIndex++;
            }
            const chosen = segTokens.slice(startIndex, endIndex + 1);
            return chosen.slice(0, maxWords).join(' ');
        }
        // fallback: join the cluster tokens directly
        return bestCluster.slice(0, maxWords).join(' ');
    }

    // Prefer original order; filter stopwords and short tokens
    const candidates = segTokens.filter(
        (t) => !STOPWORDS.has(safeLower(t)) && !/^[^A-Za-z0-9]+$/.test(t)
    );
    if (candidates.length === 0) {
        const raw = tokensOf(trimmed);
        return raw.slice(0, maxWords).join(' ');
    }

    // If the first candidate is a generic place noun (gallery, museum, studio, etc.), prefer
    // returning just the leading article + noun (if the original had an article) or the noun alone.
    const GENERIC_PLACE_NOUNS = new Set([
        'gallery',
        'museum',
        'studio',
        'centre',
        'center',
        'park',
        'market',
        'theatre',
        'theater',
        'school',
        'hall',
        'house',
        'place',
        'apartment',
        'villa',
        'hotel',
        'restaurant',
        'bar',
        'cafe',
    ]);
    const firstCandidate = candidates[0] ?? '';
    const firstCandidateLc = safeLower(firstCandidate);
    if (GENERIC_PLACE_NOUNS.has(firstCandidateLc)) {
        // find index of the noun in the original tokens to preserve original casing and any leading article
        const nounIndex = segTokens.findIndex((t) => t === firstCandidate);
        if (nounIndex > 0) {
            const prev = segTokens[nounIndex - 1] ?? '';
            if (/^(the|a|an)$/i.test(prev)) {
                return `${prev} ${segTokens[nounIndex] ?? firstCandidate}`;
            }
        }
        return (segTokens[nounIndex] ?? firstCandidate) as string;
    }

    // If bestSegment looks like a brand short token (all-caps single token), return as-is
    if (containsAllCapsToken(bestSegment ?? '') && segTokens.length <= 3) {
        // return up to maxWords tokens preserving case
        return segTokens.slice(0, maxWords).join(' ');
    }

    // Deduplicate candidates while preserving order (e.g., "garden house garden" -> "garden house")
    const uniqueCandidates = candidates.filter(
        (v, i) => candidates.indexOf(v) === i
    );

    // If we have 3+ unique tokens and some earlier token repeats in the original candidates
    // (pattern like "A B A C" -> unique [A,B,C]), prefer the concise name by dropping the
    // trailing token C (-> [A,B]). This handles cases like "A garden house in a garden city".
    if (uniqueCandidates.length >= 3) {
        for (const t of uniqueCandidates.slice(0, -1)) {
            const count = candidates.filter((c) => c === t).length;
            if (count > 1) {
                uniqueCandidates.pop();
                break;
            }
        }
    }

    // final: keep the first up-to-maxWords unique candidate tokens in original order
    return finalizeOutput(uniqueCandidates.slice(0, maxWords).join(' '));
}

/** helper: capitalize a token reasonably */
function capitalize(s: string) {
    if (!s) return s;
    return s.replace(/^./u, (c) => c.toUpperCase());
}

/** helper: find first index of subarray within array or -1 */
function indexOfSubarray<T>(arr: T[], sub: T[]): number {
    for (let i = 0; i <= arr.length - sub.length; i++) {
        let ok = true;
        for (let j = 0; j < sub.length; j++) {
            if (arr[i + j] !== sub[j]) {
                ok = false;
                break;
            }
        }
        if (ok) return i;
    }
    return -1;
}

/**
 * Truncates a string to a specified character limit and appends an ellipsis if truncated.
 */
// eslint-disable-next-line import/no-unused-modules
export const truncateString = (str: string, num: number) => {
    if (str.length <= num) {
        return str;
    }
    return str.slice(0, num) + '...';
};
